import React from 'react'
import { dispatchCustomEvent, FREQUENCY, ANALYTICS_EVENT_NAME } from '@classy/campaign-page-blocks'
import { useTranslation } from 'next-i18next'
import * as Styled from './StickyCtaButton.styled'
import { EVENT } from 'models/event'

export const useCtaButtonInView = (): [boolean, string] => {
  const hasTrackedViewRef = React.useRef(false)
  const { t } = useTranslation('blocks.donation')

  const [isAnyTargetInView, setIsAnyTargetInView] = React.useState(true)
  const [buttonLabel, setButtonLabel] = React.useState(
    t('CallToActionButton.checkoutLinkButton.donate', 'Donate'),
  )

  React.useEffect(() => {
    const ctaButtons = document.querySelectorAll('[data-id="cp-donation-form-donate"]')
    const buttonCount = ctaButtons.length

    const impactButtons = document.querySelectorAll('[data-id="cp-impact-donate"]')
    const impactButtonCount = impactButtons.length
    const donationAmountGrid = document.querySelector('[data-id="cp-donation-amount-grid"]')

    if (buttonCount !== 1 || impactButtonCount !== 0 || !donationAmountGrid) return

    const el = ctaButtons[0]

    if (el.tagName.toUpperCase() === 'BUTTON' || !(el instanceof HTMLAnchorElement)) return

    // * Tracks visibility per target (element → boolean); sticky shows only when none are visible.
    const vis = new Map<Element, boolean>([
      [donationAmountGrid, false],
      [el, false],
    ])

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => vis.set(en.target, en.isIntersecting))
        const isAnyTargetInView = [...vis.values()].some(Boolean)
        const checkoutHref = new URL(el.href)
        const donationFrequency = checkoutHref.searchParams.get('frequency') as string | null
        const label =
          !donationFrequency || donationFrequency === FREQUENCY.ONE_TIME
            ? t('CallToActionButton.checkoutLinkButton.donate', 'Donate')
            : t(
                `CallToActionButton.checkoutLinkButton.donate.${donationFrequency}`,
                `Donate ${donationFrequency?.toLowerCase()}`,
              )

        setButtonLabel(label)
        // * sticky shows when !inView (neither is visible)
        setIsAnyTargetInView(isAnyTargetInView)

        // * fire component viewed event on a single user view
        if (!isAnyTargetInView && !hasTrackedViewRef.current) {
          dispatchCustomEvent(EVENT.ANALYTICS_TRACK_EVENT, {
            value: label,
            trackingId: 'checkout sticky button: component viewed',
            eventName: ANALYTICS_EVENT_NAME.COMPONENT_VIEWED,
          })

          hasTrackedViewRef.current = true
        }
      },
      { threshold: 0.1 },
    )

    // Observe both the donation grid and the CTA anchor explicitly
    observer.observe(donationAmountGrid)
    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [t])

  return [isAnyTargetInView, buttonLabel]
}

export const scrollToDonationCtaButton = (ctaButton: HTMLAnchorElement) => {
  const prev = ctaButton.style.scrollMarginBottom
  ctaButton.style.scrollMarginBottom = '24px'

  // * Use scrollIntoView on the nearest scroll container with a 24px bottom offset
  try {
    ctaButton.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
  } catch {
    // Fallback to window scrolling if the document is the scroller
    const rect = ctaButton.getBoundingClientRect()
    const targetBottomAbs = rect.bottom + window.scrollY
    const desiredBottomAbs = window.scrollY + window.innerHeight - 24
    const newTop = window.scrollY + (targetBottomAbs - desiredBottomAbs)
    window.scrollTo({ top: newTop, behavior: 'smooth' })
  } finally {
    // * Restore original margin after the scroll completes
    setTimeout(() => {
      ctaButton.style.scrollMarginBottom = prev
    }, 600)
  }
}

export const StickyCtaButton = () => {
  const [isAnyTargetInView, buttonLabel] = useCtaButtonInView()

  const handleClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const ctaButton = document.querySelector('[data-id="cp-donation-form-donate"]')

    if (!(ctaButton instanceof HTMLAnchorElement)) return
    e.preventDefault()

    const checkoutHref = new URL(ctaButton.href)

    // * If an amount is selected, redirect to checkout
    if (checkoutHref.searchParams.has('amount')) {
      dispatchCustomEvent(EVENT.ANALYTICS_TRACK_EVENT, {
        value: checkoutHref.searchParams.get('amount'),
        trackingId: 'checkout sticky button: redirect to checkout',
        eventName: ANALYTICS_EVENT_NAME.BUTTON_CLICKED,
      })

      window.location.href = checkoutHref.href
    } else {
      // * Event sending Sticky CTA Button location and scroll position
      const stickyRect = e.currentTarget.getBoundingClientRect()
      dispatchCustomEvent(EVENT.ANALYTICS_TRACK_EVENT, {
        value: {
          // * scrollY: the window scroll offset (how far down the page the user is)
          scrollY: Math.round(window.scrollY),
          // * absoluteTop: the button’s absolute Y coordinate on the page (rect.top + scrollY)
          absoluteTop: Math.round(stickyRect.top + window.scrollY),
        },
        trackingId: 'checkout sticky button: scroll to donation grid cta',
        eventName: ANALYTICS_EVENT_NAME.BUTTON_CLICKED,
      })

      // * If there is no amount selected, scroll to the Donation Grid
      scrollToDonationCtaButton(ctaButton)
    }
  }, [])

  const isVisible = !isAnyTargetInView

  return (
    <Styled.StickyDrawer isVisible={isVisible} aria-hidden={!isVisible}>
      <Styled.StickyCtaButton
        type="button"
        onClick={handleClick}
        tabIndex={isVisible ? 0 : -1}
        aria-disabled={!isVisible}
      >
        {buttonLabel}
      </Styled.StickyCtaButton>
    </Styled.StickyDrawer>
  )
}
